import { Suspense } from "react";
import { Outlet, Route, Routes } from "react-router";
import { AuthorizationFallback } from "~/base";
import { AdminDataProvider } from "~/common/contexts";
import { PageLoading } from "~/components/loading";
import { CategoriesRoutes } from "~/features/admin/categories";
import { GroupsRoutes } from "~/features/admin/groups";
import { OrganizationsRoutes } from "~/features/admin/organizations";
import { ProductsRoutes } from "~/features/admin/products";
import { RecordMetadataSchemaRoutes } from "~/features/admin/recordMetadataSchema";
import { RelationTypesRoutes } from "~/features/admin/relationTypes";
import { TenantsRoutes } from "~/features/admin/tenants";
import { UsersRoutes } from "~/features/admin/users";
import { Authorization, useIsActiveRolloAdmin } from "~/features/authz/authorization";
import { DeweyHomeRedirect } from "~/features/authz/DeweyHomeRedirect";
import { HasuraEditorOutlet } from "~/features/authz/HasuraEditorOutlet";
import { ChangeRequestsRoutes } from "~/features/changeRequests";
import { CompoundEntitiesRoutes } from "~/features/compoundEntities";
import { CompoundRecordComposersRoutes } from "~/features/compoundRecordComposers";
import { CompoundRecordVersionsRoutes } from "~/features/compoundRecordVersions";
import { DataImportProjectsRoutes } from "~/features/dataImportProjects";
import { DataSourceTemplatesRoutes } from "~/features/dataSourceTemplates";
import { DbpjkitWorkerJobsRoutes } from "~/features/dbpjkitWorkerJobs";
import { EntitiesRoutes } from "~/features/entities";
import { EvidencesRoutes } from "~/features/evidences";
import { GeoItemCreatePage, GeoItemsRoutes } from "~/features/geoItems";
import { GeoreferencerRoutes } from "~/features/georeferencer";
import { AreaInputRoutes } from "~/features/recordGeoItems";
import { ReferencesRoutes } from "~/features/references";
import { RelationsRoutes } from "~/features/relations";
import { V2Placeholder } from "./components/V2Placeholder";

/** V2 route tree, mounted at the application root. Unrouted paths fall through to `V2Placeholder`. */
export function V2Routes() {
  return (
    <Routes>
      <Route index element={<DeweyHomeRedirect />} />

      <Route element={<HasuraEditorOutlet />}>
        <Route path="entities/*" element={<EntitiesRoutes />} />
        <Route path="relations/*" element={<RelationsRoutes />} />
        <Route path="references/*" element={<ReferencesRoutes />} />
      </Route>
      <Route path="compound-entities/*" element={<CompoundEntitiesRoutes />} />
      <Route path="compound-record-composers/*" element={<CompoundRecordComposersRoutes />} />
      <Route path="compound-record-versions/*" element={<CompoundRecordVersionsRoutes />} />
      <Route path="evidences/*" element={<EvidencesRoutes />} />
      <Route path="georeferencer/*" element={<GeoreferencerRoutes />} />
      <Route path="geo-items/*" element={<GeoItemsRoutes />} />
      <Route path="area-input/create" element={<GeoItemCreatePage />} />
      <Route path="area-input/*" element={<AreaInputRoutes />} />
      <Route path="data-import-projects/*" element={<DataImportProjectsRoutes />} />
      <Route path="change-requests/*" element={<ChangeRequestsRoutes />} />
      <Route path="dbpjkit-worker-jobs/*" element={<DbpjkitWorkerJobsRoutes />} />
      <Route path="data-source-templates/*" element={<DataSourceTemplatesRoutes />} />

      <Route path="admin" element={<AdminOnlyOutlet />}>
        <Route index element={<V2Placeholder />} />
        <Route path="organizations/*" element={<RolloAdminOnlyOrganizations />} />
        <Route path="tenants/*" element={<RolloAdminOnlyTenants />} />
        <Route path="categories/*" element={<CategoriesRoutes />} />
        <Route path="record-metadata-schema/*" element={<RecordMetadataSchemaRoutes />} />
        <Route path="products/*" element={<ProductsRoutes />} />
        <Route path="relation-types/*" element={<RelationTypesRoutes />} />
        <Route path="users/*" element={<UsersRoutes />} />
        <Route path="groups/*" element={<GroupsRoutes />} />
      </Route>

      <Route path="*" element={<V2Placeholder />} />
    </Routes>
  );
}

function AdminOnlyOutlet() {
  return (
    <Authorization allowedRoles={["admin"]} forbiddenFallback={<AuthorizationFallback />}>
      <Suspense fallback={<PageLoading />}>
        <AdminDataProvider>
          <Outlet />
        </AdminDataProvider>
      </Suspense>
    </Authorization>
  );
}

function RolloAdminOnlyTenants() {
  const isActiveRolloAdmin = useIsActiveRolloAdmin();

  return (
    <Authorization policyCheck={isActiveRolloAdmin} forbiddenFallback={<AuthorizationFallback />}>
      <TenantsRoutes />
    </Authorization>
  );
}

function RolloAdminOnlyOrganizations() {
  const isActiveRolloAdmin = useIsActiveRolloAdmin();

  return (
    <Authorization policyCheck={isActiveRolloAdmin} forbiddenFallback={<AuthorizationFallback />}>
      <OrganizationsRoutes />
    </Authorization>
  );
}
